import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { BsPencil, BsTrash } from 'react-icons/bs';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import StarDivider from '../components/uiComponents/StarDivider';
import DeleteModal from '../components/DeleteModal';
import Toast from '../components/Toast';
import Pagination from '../components/Pagination';
import settingService from '../api/settingService';

const SECTION_OPTIONS = [
  { value: 'semaine_matinale', label: 'Semaine Matinale' },
  { value: 'semence_matinale', label: 'Semence Matinale' },
  { value: 'enseignements', label: 'Enseignements' },
  { value: 'temoignages', label: 'Témoignages' },
  { value: 'prieres', label: 'Prières' },
  { value: 'evangile_du_jour', label: 'Évangile du jour' },
];

const EMPTY_FORM = { section: '', nom: '', valeur: '', date: '' };

function extractError(err) {
  if (err?.response?.data?.errors?.length)
    return err.response.data.errors.map((e) => e.message).join(', ');
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const sectionLabel = (v) => SECTION_OPTIONS.find((s) => s.value === v)?.label || v;

export default function ParametresPage() {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, currentPage: 1 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list');
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });

  const [filters, setFilters] = useState({ section: '', nom: '', page: 1, limit: 20 });

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });
  const hideToast = () => setToast({ message: '', type: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, limit: filters.limit };
      if (filters.section) params.section = filters.section;
      if (filters.nom) params.nom = filters.nom;
      const { data: res } = await settingService.list(params);
      setData(res.data);
      setPagination(res.pagination || { total: 0, totalPages: 1, currentPage: 1 });
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterChange = (e) =>
    setFilters((f) => ({ ...f, [e.target.name]: e.target.value, page: 1 }));

  const handleFormChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setFormErrors((fe) => ({ ...fe, [e.target.name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.section) errors.section = 'Section requise';
    if (!form.nom) errors.nom = 'Nom requis';
    if (!form.valeur) errors.valeur = 'Valeur requise';
    if (!form.date) errors.date = 'Date requise';
    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    setSaving(true);
    try {
      if (view === 'add') {
        await settingService.create(form);
        showToast('Paramètre ajouté avec succès');
      } else {
        await settingService.update(editItem.id, form);
        showToast('Paramètre mis à jour avec succès');
      }
      setView('list');
      setForm(EMPTY_FORM);
      setEditItem(null);
      setFormErrors({});
      fetchData();
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleEditOpen = (row) => {
    setForm({
      section: row.section,
      nom: row.nom,
      valeur: row.valeur,
      date: row.date?.slice(0, 10) || '',
    });
    setEditItem(row);
    setFormErrors({});
    setView('edit');
  };

  const handleDelete = async () => {
    try {
      await settingService.remove(deleteId);
      showToast('Paramètre supprimé avec succès');
      setDeleteId(null);
      fetchData();
    } catch (err) {
      showToast(extractError(err), 'danger');
      setDeleteId(null);
    }
  };

  return (
    <div>
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />

      <div className="text-center mb-3">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400 }}>PARAMÈTRES GÉNÉRAUX</h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded mb-4" style={{ border: '1px solid #eee' }}>
        <div className="d-md-none d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer', marginBottom: filtersOpen ? 12 : 0 }}
          onClick={() => setFiltersOpen((v) => !v)}>
          <span className="fw-semibold" style={{ fontSize: 14 }}>Filtres de recherche</span>
          <span>{filtersOpen ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}</span>
        </div>
        <div className={filtersOpen ? '' : 'd-none d-md-block'}>
          <Row>
            <Col md={10} xs={12}>
              <Row className="gy-1 gx-3">
                <Col md={4} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Section</Form.Label>
                  <Form.Select name="section" value={filters.section} onChange={handleFilterChange} size="sm">
                    <option value="">Toutes les sections</option>
                    {SECTION_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Form.Select>
                </Col>
                <Col md={4} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Nom</Form.Label>
                  <Form.Control name="nom" value={filters.nom} onChange={handleFilterChange} size="sm" placeholder="Rechercher par nom..." />
                </Col>
              </Row>
            </Col>
            <Col md={2} xs={12} className="d-flex align-items-end justify-content-end mt-2 mt-md-0">
              <Button onClick={fetchData} style={{ backgroundColor: '#8b1512', border: 'none', letterSpacing: 1 }} size="sm">RECHERCHER</Button>
            </Col>
          </Row>
        </div>
      </div>

      {/* Add/Edit form */}
      {(view === 'add' || view === 'edit') && (
        <div style={{ maxWidth: 700, margin: '0 auto 24px' }}>
          <h6 className="fw-bold mb-2">{view === 'add' ? 'Ajouter un paramètre' : 'Modifier le paramètre'}</h6>
          <Form className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>

            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Section :</Form.Label>
              <Col sm={8}>
                <Form.Select name="section" value={form.section} onChange={handleFormChange} isInvalid={!!formErrors.section}>
                  <option value="">— Sélectionner la section —</option>
                  {SECTION_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{formErrors.section}</Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Nom :</Form.Label>
              <Col sm={8}>
                <Form.Control name="nom" value={form.nom} onChange={handleFormChange} placeholder="Ex: Programme hebdomadaire" isInvalid={!!formErrors.nom} />
                <Form.Control.Feedback type="invalid">{formErrors.nom}</Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Valeur :</Form.Label>
              <Col sm={8}>
                <Form.Control as="textarea" rows={3} name="valeur" value={form.valeur} onChange={handleFormChange} placeholder="Contenu du paramètre..." isInvalid={!!formErrors.valeur} />
                <Form.Control.Feedback type="invalid">{formErrors.valeur}</Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Date :</Form.Label>
              <Col sm={8}>
                <Form.Control type="date" name="date" value={form.date} onChange={handleFormChange} isInvalid={!!formErrors.date} />
                <Form.Control.Feedback type="invalid">{formErrors.date}</Form.Control.Feedback>
              </Col>
            </Form.Group>

            <div className="text-center mt-4 d-flex gap-3 justify-content-center">
              <Button size="sm" variant="outline-secondary" type="button"
                onClick={() => { setView('list'); setForm(EMPTY_FORM); setFormErrors({}); }}
                style={{ letterSpacing: 1, paddingInline: 24 }}>ANNULER</Button>
              <Button size="sm" type="button" disabled={saving} onClick={handleSave}
                style={{ backgroundColor: '#8b1512', border: 'none', letterSpacing: 1, paddingInline: 40 }}>
                {saving ? <Spinner size="sm" animation="border" /> : view === 'add' ? 'ENREGISTRER' : 'ENREGISTRER LES MODIFICATIONS'}
              </Button>
            </div>
          </Form>
        </div>
      )}

      {/* List */}
      {view === 'list' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h6 style={{ fontWeight: 700, margin: 0 }}>Résultats ({pagination.total ?? data.length})</h6>
            <Button size="sm" style={{ backgroundColor: '#8b1512', border: 'none' }}
              onClick={() => { setForm(EMPTY_FORM); setFormErrors({}); setView('add'); }}>+ Nouveau Paramètre</Button>
          </div>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: '#8b1512' }} /></div>
          ) : data.length === 0 ? (
            <div className="text-center py-5 text-secondary">Aucun paramètre trouvé.</div>
          ) : (
            <>
              {/* Desktop table */}
              <div style={{ overflowX: 'auto' }} className="d-none d-md-block table-mcm-wrap">
                <table className="table-mcm">
                  <thead>
                    <tr>
                      {['Section', 'Date', 'Nom', 'Valeur', 'Actions'].map((h) => (
                        <th key={h} className="th-mcm">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row.id}>
                        <td className="td-mcm">{sectionLabel(row.section)}</td>
                        <td className="td-mcm" style={{ whiteSpace: 'nowrap' }}>{formatDate(row.date)}</td>
                        <td className="td-mcm">{row.nom}</td>
                        <td className="td-mcm">{row.valeur}</td>
                        <td className="td-mcm" style={{ whiteSpace: 'nowrap' }}>
                          <button className="icon-btn-mcm" onClick={() => handleEditOpen(row)}><BsPencil size={15} color="#555" /></button>
                          <button className="icon-btn-mcm" onClick={() => setDeleteId(row.id)}><BsTrash size={15} color="#8b1512" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="d-md-none" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {data.map((row) => (
                  <div key={row.id} className="mobile-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontWeight: 600, color: '#8b1512' }}>{sectionLabel(row.section)}</span>
                      <span style={{ color: '#888', fontSize: 12 }}>{formatDate(row.date)}</span>
                    </div>
                    <div style={{ marginBottom: 2 }}><span className="mobile-label">Nom: </span>{row.nom}</div>
                    <div style={{ marginBottom: 8 }}><span className="mobile-label">Valeur: </span>{row.valeur}</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="icon-btn-mcm" onClick={() => handleEditOpen(row)}><BsPencil size={15} color="#555" /></button>
                      <button className="icon-btn-mcm" onClick={() => setDeleteId(row.id)}><BsTrash size={15} color="#8b1512" /></button>
                    </div>
                  </div>
                ))}
              </div>

              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-center gap-2 mt-3">
                  <Button size="sm" variant="outline-secondary" disabled={filters.page <= 1}
                    onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}>‹ Précédent</Button>
                  <span className="align-self-center small text-secondary">Page {filters.page} / {pagination.totalPages}</span>
                  <Button size="sm" variant="outline-secondary" disabled={filters.page >= pagination.totalPages}
                    onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}>Suivant ›</Button>
                </div>
              )}
            </>
          )}
          <Pagination
            currentPage={filters.page}
            totalPages={pagination.totalPages}
            onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          />
        </>
      )}

      <DeleteModal
        show={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        message="Êtes-vous sûr de vouloir supprimer ce paramètre ?"
      />
    </div>
  );
}
